import React from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import ReplayIcon from '@mui/icons-material/Replay';

const STATUS_LABELS = {
  queued: 'Wartet',
  processing: 'In Bearbeitung',
  canceled: 'Abgebrochen',
  completed: 'Fertig',
  failed: 'Fehlgeschlagen',
};

const STAGE_LABELS = {
  audio_load: 'Audio laden',
  transcribe: 'Whisper',
  align: 'Alignment',
  diarize: 'Sprechererkennung',
  postprocess: 'Nachbearbeitung',
  done: 'Abgeschlossen',
};

const STATUS_CHIP_COLOR = {
  queued: 'default',
  processing: 'primary',
  completed: 'success',
  failed: 'error',
  canceled: 'warning',
};

function formatClock(value) {
  if (!value) return null;
  const normalizedValue = (
    typeof value === 'string' && !/[zZ]|[+-]\d{2}:\d{2}$/.test(value)
      ? `${value}Z`
      : value
  );
  const date = new Date(normalizedValue);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

function formatDuration(seconds) {
  const numeric = Number(seconds || 0);
  if (!numeric) return null;
  if (numeric < 90) return `${Math.max(1, Math.round(numeric))} Sek.`;
  if (numeric < 3600) return `${Math.round(numeric / 60)} Min.`;
  const hours = Math.floor(numeric / 3600);
  const minutes = Math.round((numeric % 3600) / 60);
  return minutes ? `${hours} Std. ${minutes} Min.` : `${hours} Std.`;
}

export default function JobQueueItem({ job, onCancel, onOpen, onDismiss }) {
  const status = job.status || 'queued';
  const isRunning = status === 'queued' || status === 'processing';
  const isDone = status === 'completed';
  const isFailed = status === 'failed' || status === 'canceled';

  const stageLabel = STAGE_LABELS[job.stage] || null;
  const overall = Math.max(0, Math.min(100, job.overallPercent || 0));

  const name = job.originalFilename || job.jobId?.slice(0, 8) || 'Unbenannt';
  const startClock = formatClock(job.estimatedStartAt);
  const finishClock = formatClock(job.estimatedFinishAt);
  const deadlineClock = formatClock(job.gpuWaitDeadlineAt);
  const startDuration = formatDuration(job.estimatedStartSeconds);
  const finishDuration = formatDuration(job.estimatedFinishSeconds);
  const isGpuWaiting = status === 'processing' && job.retrying;
  const queuedHelp = status === 'queued'
    ? 'Läuft im Hintergrund weiter. Du musst diese Seite nicht offen lassen.'
    : null;
  const gpuHelp = isGpuWaiting
    ? `Wartet auf freien GPU-Speicher. ${
        deadlineClock ? `Es wird bis ca. ${deadlineClock} weiter versucht. ` : ''
      }Du kannst die Seite schließen und später zurückkommen.`
    : null;

  return (
    <Box sx={{ py: 1.25 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={600} noWrap title={name}>
            {name}
          </Typography>
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            useFlexGap
            sx={{ mt: 0.25, flexWrap: 'wrap' }}
          >
            <Chip
              size="small"
              label={STATUS_LABELS[status] || status}
              color={STATUS_CHIP_COLOR[status] || 'default'}
              variant={isDone ? 'filled' : 'outlined'}
              sx={{ height: 20 }}
            />
            {isRunning && stageLabel && (
              <Typography variant="caption" color="text.secondary" noWrap>
                {stageLabel}
              </Typography>
            )}
            {status === 'queued' && job.queuePosition ? (
              <Typography variant="caption" color="text.secondary">
                Position {job.queuePosition}
              </Typography>
            ) : null}
            {startClock && status === 'queued' && (
              <Typography variant="caption" color="text.secondary">
                Start ca. {startClock}
              </Typography>
            )}
            {finishClock && isRunning && (
              <Typography variant="caption" color="text.secondary">
                Fertig ca. {finishClock}
              </Typography>
            )}
            {job.retrying && (
              <Tooltip title={job.retryReason || 'Erneuter Versuch'}>
                <Chip
                  size="small"
                  icon={<ReplayIcon fontSize="small" />}
                  label="Retry"
                  color="warning"
                  variant="outlined"
                  sx={{ height: 20 }}
                />
              </Tooltip>
            )}
          </Stack>
          {(queuedHelp || gpuHelp) && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 0.45, lineHeight: 1.35 }}
            >
              {gpuHelp || queuedHelp}
            </Typography>
          )}
          {isGpuWaiting && startDuration && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 0.2, lineHeight: 1.35 }}
            >
              Voraussichtlicher Start in ca. {startDuration}
              {finishDuration ? `, Abschluss in ca. ${finishDuration}.` : '.'}
            </Typography>
          )}
        </Box>

        {isRunning && <CircularProgress size={16} thickness={5} />}

        {isDone && onOpen && (
          <Tooltip title="Ergebnis öffnen">
            <IconButton size="small" onClick={() => onOpen(job)}>
              <OpenInNewRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {isRunning && onCancel && (
          <Tooltip title="Job abbrechen">
            <IconButton size="small" onClick={() => onCancel(job)}>
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {(isDone || isFailed) && onDismiss && (
          <Tooltip title="Aus Liste entfernen">
            <IconButton size="small" onClick={() => onDismiss(job)}>
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      {isRunning && (
        <Box sx={{ mt: 0.75 }}>
          <LinearProgress
            variant={overall > 0 ? 'determinate' : 'indeterminate'}
            value={overall}
            sx={{ height: 6, borderRadius: 3 }}
          />
          {overall > 0 && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.25, display: 'block' }}
            >
              {overall}%
            </Typography>
          )}
        </Box>
      )}

      {isFailed && job.error?.user_message && (
        <Typography variant="caption" color="error.main" sx={{ mt: 0.5, display: 'block' }}>
          {job.error.user_message}
        </Typography>
      )}
    </Box>
  );
}
